import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqData = [
  {
    question: "What is a Liquidity Audit?",
    answer: "A Liquidity Audit is a free 60-second diagnostic tool that shows Ontario homeowners whether their current mortgage structure is costing them more than it should — and where cash flow can be recovered or optimized."
  },
  {
    question: "How do self-employed borrowers qualify for a mortgage in Canada?",
    answer: "Lenders qualify self-employed borrowers based on personal declared income from your T1 — not what your business earns. If you pay yourself conservatively to minimize taxes, your qualifying income on paper may look very different from your actual financial position. Understanding that gap early makes the process significantly smoother."
  },
  {
    question: "What is the difference between mortgage approval and what I can actually afford?",
    answer: "Your approval amount is the ceiling the bank will lend you based on income and debt ratios. It does not account for childcare, variable income months, lifestyle costs, or financial goals. The number you can comfortably carry is often meaningfully lower — and that's the number worth knowing before you start shopping."
  },
  {
    question: "Can I get a mortgage if I pay myself dividends from my corporation?",
    answer: "Yes — but the qualification works differently than T4 employment income. Most lenders use a 2-year average of personal income from your NOA. Some lenders have Business for Self programs that allow for stated income. The key is working with someone who understands incorporated borrower files specifically."
  },
  {
    question: "What happens when I switch lenders at renewal in Ontario?",
    answer: "Switching lenders at renewal is treated as a new application — full income verification, credit pull, updated debt ratios, and stress test at today's qualifying rate. If your financial picture has changed since you first bought (new business, income shift, more debt), it is worth reviewing your position before your renewal window opens."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-start justify-between text-left transition-colors hover:bg-slate-50/50"
        aria-expanded={isOpen}
      >
        <span className="font-heading font-semibold text-navy-950 pr-8 text-base md:text-lg">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-slate-600 leading-relaxed text-sm md:text-base">
          {answer}
        </p>
      </div>
    </div>
  );
};

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white border-t border-slate-200" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600">
            Common questions about mortgages, cash flow, and working with a mortgage professional in Ontario.
          </p>
        </div>

        {/* FAQ List */}
        <div className="bg-slate-50 rounded-xl px-6 md:px-8">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
